import React, { useState } from 'react';
import { Copy, Sprout } from 'lucide-react';

import Modal from '../../ui/Modal';
import Input from '../../ui/Input';
import Spinner from '../../ui/Spinner';
import Badge from '../../ui/Badge';
import { useToast } from '../../ui/Toast';
import useProductSearch from '../../../hooks/useProductSearch';
import { primaryPhoto } from '../../../utils/photos';
import { cloudinaryThumb, cloudinaryThumbSrcSet } from '../../../utils/cloudinary';

// Catalog browser to grab a product's public URL without leaving the print
// page — filter by name, copy the link (same origin/products/:id shape as
// AdminProducts' copyLink), then paste it into the QR value field (or
// anywhere else) by hand. See blocks/qr-block.md "Composes with": whatever
// this URL ends up in is still just a hand-typed-looking `value` string, no
// schema change.
export default function ProductPickerModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const { items, loading } = useProductSearch(query, { enabled: isOpen });
  const { addToast } = useToast();

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const copyLink = async (product) => {
    const url = `${window.location.origin}/products/${product._id}`;
    try {
      await navigator.clipboard.writeText(url);
      addToast('Link copied!', { variant: 'success' });
    } catch {
      addToast('Could not copy link', { variant: 'error' });
      return;
    }
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Copy product link">
      <Input
        label="Filter by name"
        type="search"
        placeholder="Search products…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className="ss-print__product-list">
        {loading ? (
          <div className="ss-print__product-list-state">
            <Spinner label="Loading products" />
          </div>
        ) : items.length === 0 ? (
          <p className="ss-print__product-list-state">
            {query.trim() ? `No products match "${query.trim()}"` : 'No products yet'}
          </p>
        ) : (
          items.map((p) => {
            const photo = primaryPhoto(p.photos);
            return (
              <button
                key={p._id}
                type="button"
                className="ss-print__product-row"
                onClick={() => copyLink(p)}
                title="Copy product link"
              >
                <span className="ss-print__product-row-icon" aria-hidden="true">
                  <Copy size={16} />
                </span>
                <span className="ss-admin-thumb">
                  {photo ? (
                    <img
                      src={cloudinaryThumb(photo.url, 80)}
                      srcSet={cloudinaryThumbSrcSet(photo.url, 80)}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <span className="ss-admin-thumb__ph">
                      <Sprout size={20} aria-hidden="true" />
                    </span>
                  )}
                </span>
                <span className="ss-print__product-row-title">{p.title}</span>
                {!p.active && <Badge className="ss-print__product-row-badge">Inactive</Badge>}
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
}
